import { useEffect, useMemo, useState } from "react";
import {
  Drawer, Form, Input, Button, Row, Col, Select, DatePicker, InputNumber,
  Card, Table, Space, Tooltip, Typography, Statistic, Alert, Modal, message
} from "antd";
import {
  PlusOutlined, DeleteOutlined, FileTextOutlined, ShoppingCartOutlined, SaveOutlined, QuestionCircleOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { createProductosPO, updateProductoPO, getUnidadesMedidaPO } from "../../../../services/financiero/ingresosPO/ordenesServicioPOService";
import { getProveedoresPO } from "../../../../services/financiero/ingresosPO/proveedoresPOService";
import { getProyectosPO, getMunicipiosPorProyectoPO } from "../../../../services/financiero/ingresosPO/catalogosPOService";
import { cardHeaderStyle, cardStyle, CardTitle, filterOption } from "../../ingresosPOCardStyles";
import styles from "../../ingresosPOStyles.module.css";

const { Text } = Typography;
const { Option } = Select;

const formatMoney = (value) =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(value || 0));

const moneyFormatter = (v) => (v === undefined || v === null || v === "" ? "" : `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","));
const moneyParser = (v) => (v || "").replace(/\$\s?|(,*)/g, "");

const noMargin = { marginBottom: 0 };

const nombreProveedor = (p) => [p.identificacion, p.nombre].filter(Boolean).join(" - ");

// Título de columna con ayuda: explica qué se espera en cada campo de la lista.
const ColTitle = ({ children, ayuda }) => (
  <Tooltip title={ayuda}>
    <span style={{ cursor: "help" }}>
      {children} <QuestionCircleOutlined style={{ fontSize: 12, opacity: 0.75 }} />
    </span>
  </Tooltip>
);

// Registro tipo "tienda": los datos de la PO se diligencian una sola vez en el
// encabezado y al guardar se replican en cada producto de la lista (la BD no
// tiene tabla de encabezado; cada producto es una fila de fin_productos).
// Con `producto` definido funciona en modo edición de ese único registro.
export default function RegistroProductosPOForm({ open, onClose, onSuccess, userLogin, initialData, producto }) {

  const [form] = Form.useForm();
  const [proveedores, setProveedores] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [municipiosProyecto, setMunicipiosProyecto] = useState([]);
  const [saving, setSaving] = useState(false);

  const esEdicion = !!producto;
  const lineas = Form.useWatch("productos", form) || [];

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [prov, uni, proy, munProy] = await Promise.all([
          getProveedoresPO(), getUnidadesMedidaPO(), getProyectosPO(), getMunicipiosPorProyectoPO()
        ]);
        setProveedores(prov || []);
        setUnidades(uni || []);
        setProyectos(proy || []);
        setMunicipiosProyecto(munProy || []);
      } catch (error) {
        console.error(error);
        message.error("Error cargando catálogos");
      }
    };
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (producto) {
      form.setFieldsValue({
        Numero_PO: producto.Numero_PO,
        oid_proveedor: producto.oid_proveedor,
        fecha_entrega_po: producto.fecha_entrega_po ? dayjs(producto.fecha_entrega_po) : null,
        observacion: producto.observacion,
        productos: [{
          oid_proyecto: producto.oid_proyecto,
          oid_municipio: producto.oid_municipio,
          nombre_producto: producto.nombre_producto,
          descripcion: producto.descripcion,
          oid_unidad_medida: producto.oid_unidad_medida,
          cantidad: producto.cantidad_comprada != null ? Number(producto.cantidad_comprada) : null,
          valor_unitario_COP: producto.valor_unitario_COP != null ? Number(producto.valor_unitario_COP) : null
        }]
      });
    } else if (initialData) {
      form.setFieldsValue({
        ...initialData.encabezado,
        productos: initialData.productos?.length ? initialData.productos : [{}]
      });
    } else {
      form.setFieldsValue({ productos: [{}] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, producto, initialData]);

  const municipiosDe = useMemo(() => {
    const mapa = {};
    municipiosProyecto.forEach((m) => {
      (mapa[m.oid_proyecto] = mapa[m.oid_proyecto] || []).push(m);
    });
    return mapa;
  }, [municipiosProyecto]);

  const handleProyectoChange = (index, oidProyecto) => {
    const opciones = municipiosDe[oidProyecto] || [];
    // Si el proyecto solo tiene un municipio se selecciona de una vez.
    form.setFieldValue(["productos", index, "oid_municipio"], opciones.length === 1 ? opciones[0].oid : undefined);
  };

  // La línea nueva hereda proyecto y municipio de la anterior: en una misma PO
  // lo habitual es que varios productos vayan al mismo destino.
  const agregarLinea = (add) => {
    const ultima = lineas[lineas.length - 1] || {};
    add({ oid_proyecto: ultima.oid_proyecto, oid_municipio: ultima.oid_municipio });
  };

  const totalGeneral = lineas.reduce(
    (acc, l) => acc + Number(l?.cantidad || 0) * Number(l?.valor_unitario_COP || 0), 0
  );
  const totalUnidades = lineas.reduce((acc, l) => acc + Number(l?.cantidad || 0), 0);

  const hayDatos = () => {
    const v = form.getFieldsValue(true);
    return !!(v.Numero_PO || v.oid_proveedor || v.observacion ||
      (v.productos || []).some((l) => l && (l.nombre_producto || l.cantidad || l.valor_unitario_COP)));
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      message.warning("Revise los campos marcados en rojo");
      return;
    }
    const encabezado = {
      Numero_PO: values.Numero_PO.trim(),
      oid_proveedor: values.oid_proveedor,
      fecha_entrega_po: values.fecha_entrega_po.format("YYYY-MM-DD"),
      observacion: values.observacion?.trim() || null
    };
    const productos = (values.productos || []).map((l) => ({
      oid_proyecto: l.oid_proyecto,
      oid_municipio: l.oid_municipio,
      nombre_producto: l.nombre_producto.trim(),
      descripcion: l.descripcion?.trim() || "",
      oid_unidad_medida: l.oid_unidad_medida,
      cantidad: Number(l.cantidad),
      valor_unitario_COP: Number(l.valor_unitario_COP)
    }));
    if (!productos.length) {
      message.warning("Agregue al menos un producto");
      return;
    }
    try {
      setSaving(true);
      if (esEdicion) {
        await updateProductoPO(producto.oid, { ...encabezado, ...productos[0] }, userLogin);
        message.success("Producto actualizado");
      } else {
        const res = await createProductosPO({ encabezado, productos }, userLogin);
        message.success(`${res.ids?.length || productos.length} producto(s) registrados en la PO ${encabezado.Numero_PO}`);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Error guardando los productos");
    } finally {
      setSaving(false);
    }
  };

  const columnas = (remove) => [
    {
      title: "#",
      width: 48,
      align: "center",
      render: (_, __, index) => <Text strong>{index + 1}</Text>
    },
    {
      title: <ColTitle ayuda="Proyecto al que se le compra el producto. Puede buscar por código o por nombre.">Proyecto</ColTitle>,
      width: 230,
      render: (_, field) => (
        <Form.Item name={[field.name, "oid_proyecto"]} rules={[{ required: true, message: "Requerido" }]} style={noMargin}>
          <Select
            showSearch
            placeholder="Código - proyecto"
            filterOption={filterOption}
            popupMatchSelectWidth={420}
            onChange={(v) => handleProyectoChange(field.name, v)}
          >
            {proyectos.map((p) => <Option key={p.oid} value={p.oid}>{p.codigo} - {p.proyecto}</Option>)}
          </Select>
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Solo aparecen los municipios asociados al proyecto elegido en esta fila.">Municipio</ColTitle>,
      width: 170,
      render: (_, field) => {
        const oidProyecto = lineas[field.name]?.oid_proyecto;
        const opciones = municipiosDe[oidProyecto] || [];
        return (
          <Form.Item name={[field.name, "oid_municipio"]} rules={[{ required: true, message: "Requerido" }]} style={noMargin}>
            <Select
              showSearch
              disabled={!oidProyecto}
              placeholder={oidProyecto ? "Municipio" : "Elija proyecto"}
              filterOption={filterOption}
              popupMatchSelectWidth={260}
              notFoundContent="El proyecto no tiene municipios asociados"
            >
              {opciones.map((m) => <Option key={m.oid} value={m.oid}>{m.municipio} ({m.departamento})</Option>)}
            </Select>
          </Form.Item>
        );
      }
    },
    {
      title: <ColTitle ayuda="Nombre corto del producto, como debe salir en la columna Bien del acta de entrega.">Producto</ColTitle>,
      width: 220,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "nombre_producto"]}
          rules={[{ required: true, whitespace: true, message: "Requerido" }]}
          style={noMargin}
        >
          <Input placeholder="Nombre del producto" autoComplete="off" />
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Características o especificaciones técnicas. Sale en la columna Descripción del acta de entrega.">Descripción</ColTitle>,
      width: 280,
      render: (_, field) => (
        <Form.Item name={[field.name, "descripcion"]} style={noMargin}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Características / especificaciones" autoComplete="off" />
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Unidad en la que se cuenta la cantidad (unidad, kilo, metro, rollo, etc.).">Unidad</ColTitle>,
      width: 140,
      render: (_, field) => (
        <Form.Item name={[field.name, "oid_unidad_medida"]} rules={[{ required: true, message: "Requerido" }]} style={noMargin}>
          <Select showSearch placeholder="Unidad" filterOption={filterOption} popupMatchSelectWidth={200}>
            {unidades.map((u) => <Option key={u.oid} value={u.oid}>{u.unidad_medida}</Option>)}
          </Select>
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Cantidad comprada en la PO. Es el stock inicial disponible para las entregas.">Cantidad</ColTitle>,
      width: 120,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "cantidad"]}
          rules={[{ required: true, message: "Requerido" }, { type: "number", min: 0.01, message: "Mayor a 0" }]}
          style={noMargin}
        >
          <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder="0" autoComplete="off" />
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Valor de una sola unidad en pesos colombianos. Escriba solo números.">Precio unitario COP</ColTitle>,
      width: 180,
      render: (_, field) => (
        <Form.Item name={[field.name, "valor_unitario_COP"]} rules={[{ required: true, message: "Requerido" }]} style={noMargin}>
          <InputNumber min={0} style={{ width: "100%" }} formatter={moneyFormatter} parser={moneyParser} placeholder="$ 0" autoComplete="off" />
        </Form.Item>
      )
    },
    {
      title: <ColTitle ayuda="Se calcula automáticamente: cantidad por precio unitario.">Valor total</ColTitle>,
      width: 150,
      align: "right",
      render: (_, field) => {
        const l = lineas[field.name] || {};
        return <Text strong>$ {formatMoney(Number(l.cantidad || 0) * Number(l.valor_unitario_COP || 0))}</Text>;
      }
    },
    ...(esEdicion ? [] : [{
      title: "",
      width: 52,
      align: "center",
      render: (_, field) => (
        <Tooltip title={lineas.length > 1 ? "Quitar producto" : "Debe quedar al menos un producto"}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={lineas.length <= 1}
            onClick={() => remove(field.name)}
          />
        </Tooltip>
      )
    }])
  ];

  // Evita perder por accidente una lista de productos a medio digitar.
  const handleCerrar = () => {
    if (esEdicion || saving || !hayDatos()) {
      onClose();
      return;
    }
    Modal.confirm({
      title: "¿Descartar el registro?",
      content: "Se perderán los datos que no ha guardado.",
      okText: "Descartar",
      okButtonProps: { danger: true },
      cancelText: "Seguir editando",
      onOk: onClose
    });
  };

  return (
    <Drawer
      title={esEdicion ? `Editar producto — PO ${producto.Numero_PO}` : "Registro de productos"}
      open={open}
      onClose={handleCerrar}
      width="min(1500px, 96vw)"
      rootClassName={styles.ingresosPoModule}
      maskClosable={false}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={handleCerrar}>Cancelar</Button>
          <Tooltip title={esEdicion ? "Guarda los cambios de este producto" : "Guarda todos los productos de la lista con los datos de la PO"}>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
              {esEdicion ? "Guardar cambios" : "Guardar productos"}
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Form form={form} layout="vertical" autoComplete="off">
        {esEdicion ? (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 10, marginBottom: 20 }}
            message="Los cambios aplican únicamente a este producto; los demás productos de la misma PO no se modifican."
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 10, marginBottom: 20 }}
            message="Diligencie una sola vez los datos de la PO y agregue abajo los productos. Al guardar, cada producto queda registrado con los datos del encabezado."
          />
        )}

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle }}
          title={<CardTitle icon={<FileTextOutlined />}>Datos de la PO</CardTitle>}
        >
          <Row gutter={[20, 4]}>
            <Col xs={24} md={6}>
              <Form.Item
                label="PO"
                name="Numero_PO"
                tooltip="Número de la orden de compra tal como aparece en el documento. Letras y números, máximo 20 caracteres."
                rules={[
                  { required: true, whitespace: true, message: "Ingrese el número de PO" },
                  { pattern: /^[A-Za-z0-9\-_/ ]+$/, message: "Solo letras y números" }
                ]}
              >
                <Input size="large" maxLength={20} placeholder="Ej: PO123456" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} md={11}>
              <Form.Item
                label="Proveedor"
                name="oid_proveedor"
                tooltip="Busque por NIT o por nombre. Si no aparece, créelo primero en el menú Proveedores."
                rules={[{ required: true, message: "Seleccione el proveedor" }]}
              >
                <Select size="large" showSearch placeholder="Buscar por NIT o nombre" filterOption={filterOption}>
                  {proveedores.map((p) => <Option key={p.oid} value={p.oid}>{nombreProveedor(p)}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item
                label="Fecha de entrega PO"
                name="fecha_entrega_po"
                tooltip="Fecha en la que el proveedor entrega los productos de esta PO."
                rules={[{ required: true, message: "Seleccione la fecha" }]}
              >
                <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="dd/mm/aaaa" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Observación"
                name="observacion"
                tooltip="Nota general de la PO (opcional). Se guarda igual en todos los productos de la lista."
                style={noMargin}
              >
                <Input.TextArea rows={2} maxLength={500} showCount placeholder="Observaciones generales de la PO (opcional)" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={{ ...cardStyle, marginBottom: 0 }}
          styles={{ header: cardHeaderStyle, body: { padding: 0 } }}
          title={<CardTitle icon={<ShoppingCartOutlined />}>{esEdicion ? "Producto" : "Productos a registrar"}</CardTitle>}
        >
          <Form.List name="productos">
            {(fields, { add, remove }) => (
              <>
                <Table
                  rowKey="key"
                  size="small"
                  dataSource={fields}
                  columns={columnas(remove)}
                  pagination={false}
                  scroll={{ x: 1560 }}
                />
                {!esEdicion && (
                  <div style={{ padding: 16 }}>
                    <Tooltip title="Agrega otra fila. Hereda el proyecto y el municipio de la fila anterior para no repetirlos.">
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => agregarLinea(add)}
                        block
                        style={{ height: 42, borderRadius: 8 }}
                      >
                        Agregar producto
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </>
            )}
          </Form.List>
          <Row justify="end" gutter={32} style={{ padding: "12px 24px 16px", borderTop: "1px solid #f0f0f0", margin: 0 }}>
            {!esEdicion && (
              <Col>
                <Statistic title="Productos" value={lineas.length} valueStyle={{ fontSize: 20 }} />
              </Col>
            )}
            <Col>
              <Statistic title="Unidades" value={totalUnidades} precision={0} groupSeparator="." valueStyle={{ fontSize: 20 }} />
            </Col>
            <Col>
              <Statistic
                title="Valor total COP"
                value={`$ ${formatMoney(totalGeneral)}`}
                valueStyle={{ fontSize: 20, color: "#042956", fontWeight: 700 }}
              />
            </Col>
          </Row>
        </Card>
      </Form>
    </Drawer>
  );
}
